import { useState, useRef } from 'react';
import { supabase } from '../../lib/supabase';

export default function CSVUploadModal({ onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const [showGuide, setShowGuide] = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }
    setFile(f);
    setError('');
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const formData = new FormData();
      formData.append('file', file);

      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const res = await fetch(`${API_URL}/api/company/ideas/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session?.access_token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');

      onUploaded(data);
      onClose();
    } catch (err) {
      setError(err.message);
    }
    setUploading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-glass max-w-lg w-full p-7" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-xl text-vault-navy">Upload CSV</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>

        {/* Drop zone */}
        <div
          className={`border-2 border-dashed rounded-2xl p-8 text-center transition-colors cursor-pointer ${
            dragOver ? 'border-vault-gold bg-amber-50' : file ? 'border-emerald-300 bg-emerald-50' : 'border-vault-border hover:border-gray-300'
          }`}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
          onClick={() => fileRef.current?.click()}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={e => handleFile(e.target.files[0])}
          />
          {file ? (
            <div>
              <div className="text-2xl mb-2">📄</div>
              <div className="text-sm font-semibold text-vault-navy">{file.name}</div>
              <div className="text-xs text-gray-400 mt-1">{(file.size / 1024).toFixed(1)} KB</div>
              <button
                onClick={(e) => { e.stopPropagation(); setFile(null); }}
                className="text-xs text-vault-error mt-2 hover:underline"
              >
                Remove
              </button>
            </div>
          ) : (
            <div>
              <div className="text-3xl mb-2">📥</div>
              <div className="text-sm text-gray-500 mb-1">Drag & drop your CSV here, or click to browse</div>
              <div className="text-xs text-gray-300">Maximum 50MB</div>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-3 text-xs text-vault-error bg-red-50 border border-red-200 rounded-xl px-3 py-2">{error}</div>
        )}

        {/* CSV format guide */}
        <div className="mt-4">
          <button onClick={() => setShowGuide(!showGuide)} className="text-xs text-vault-gold font-semibold hover:underline">
            {showGuide ? 'Hide' : 'Show'} CSV format guide
          </button>
          {showGuide && (
            <div className="mt-2 bg-gray-50 rounded-xl p-4 text-xs text-gray-500">
              <p className="mb-2">Your CSV should have columns for:</p>
              <p className="font-medium text-gray-600 mb-2">Title, Description, Inventor Name, Email, Category, Source, Date Received, Status, Notes</p>
              <p className="mb-2">Column names are flexible — our parser handles variations like "Product Name", "Concept", "Submitted By", etc.</p>
              <div className="overflow-x-auto mt-2">
                <table className="text-[11px] border border-vault-border">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-2 py-1 border-r border-vault-border">Title</th>
                      <th className="px-2 py-1 border-r border-vault-border">Description</th>
                      <th className="px-2 py-1 border-r border-vault-border">Inventor</th>
                      <th className="px-2 py-1">Category</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-2 py-1 border-r border-vault-border">Dino Builder</td>
                      <td className="px-2 py-1 border-r border-vault-border">Magnetic dinosaur construction set</td>
                      <td className="px-2 py-1 border-r border-vault-border">Jane Smith</td>
                      <td className="px-2 py-1">Construction</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-2 py-1 border-r border-vault-border">Glow Puzzle</td>
                      <td className="px-2 py-1 border-r border-vault-border">Glow-in-the-dark jigsaw for ages 3-5</td>
                      <td className="px-2 py-1 border-r border-vault-border">Mike Chen</td>
                      <td className="px-2 py-1">Puzzles</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-btn text-sm font-semibold text-gray-400 border-2 border-vault-border">
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="flex-1 py-2.5 rounded-btn text-sm font-semibold text-white disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
          >
            {uploading ? 'Uploading...' : 'Upload & Process'}
          </button>
        </div>
      </div>
    </div>
  );
}
